import { z } from 'zod';
import { paginationQueryFor } from '../common/pagination.js';
import { phoneSchema, storedPhoneSchema } from '../phone/phone.js';
export const documentTypeSchema = z.enum(['NATIONAL_ID', 'PASSPORT']);
export const customerLevelSchema = z.enum(['A', 'B', 'C']);
const customerFieldsSchema = z.object({
    /** Any form the shared phone rule accepts; the server stores E.164 (customers.md section 9). */
    phone: phoneSchema,
    fullName: z.string().trim().min(1).max(200).optional(),
    documentType: documentTypeSchema.optional(),
    documentNumber: z.string().trim().min(1).max(64).optional(),
    documentCountry: z
        .string()
        .trim()
        .length(2)
        .regex(/^[A-Z]{2}$/, 'must be an ISO alpha-2 country code')
        .optional(),
    categoryId: z.string().optional(),
    /**
     * The WhatsApp verification of the phone (customers.md section 10.4),
     * required when the setting is on and the phone needs a code.
     */
    phoneVerificationId: z.string().min(1).max(64).optional(),
});
function withCompleteDocument(schema) {
    return schema.refine((v) => !v.documentType || Boolean(v.documentNumber && v.documentCountry), {
        message: 'documentNumber and documentCountry are required once a document type is chosen',
        path: ['documentNumber'],
    });
}
export const createCustomerSchema = withCompleteDocument(customerFieldsSchema);
/**
 * An edit reads a stored phone of an excluded country too; the service
 * refuses it with PHONE_COUNTRY_NOT_ALLOWED only when it changed
 * (customers.md section 9.7).
 */
export const updateCustomerSchema = withCompleteDocument(customerFieldsSchema.extend({ phone: storedPhoneSchema }));
export const customerListQuerySchema = paginationQueryFor([
    'id',
    'phone',
    'fullName',
    'documentType',
    'documentNumber',
    'documentCountry',
    'level',
    'categoryId',
    'createdByName',
    'createdSystem',
    'createdAt',
    'updatedAt',
    'deletedAt',
]).extend({
    level: customerLevelSchema.optional(),
    categoryId: z.string().optional(),
    systemCode: z.string().optional(),
    deleted: z.coerce.boolean().optional(),
    dateFrom: z.iso.date().optional(),
    dateTo: z.iso.date().optional(),
});
export const customerMovementsQuerySchema = paginationQueryFor([
    'id',
    'systemCode',
    'branchId',
    'type',
    'reference',
    'amount',
    'currency',
    'occurredAt',
    'createdAt',
    'employeeName',
    'fieldEmployeeName',
    'reversedAt',
    'reversedByName',
]).extend({
    systemCode: z.string().optional(),
    dateFrom: z.iso.date().optional(),
    dateTo: z.iso.date().optional(),
});
export const customerViewSchema = z.object({
    id: z.string(),
    phone: z.string(),
    /** Set when a verification receipt was applied to the current phone (section 10.4). */
    phoneVerifiedAt: z.string().nullable().optional(),
    fullName: z.string().nullable(),
    documentType: documentTypeSchema.nullable(),
    documentNumber: z.string().nullable(),
    documentCountry: z.string().nullable(),
    level: customerLevelSchema,
    categoryId: z.string().nullable(),
    createdBy: z.string(),
    updatedBy: z.string(),
    createdByName: z.string().nullable().optional(),
    updatedByName: z.string().nullable().optional(),
    createdSystem: z.string().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.string().nullable(),
});
/** إعدادات العملاء (customers.md section 3.4a). */
export const customerSettingsSchema = z.object({
    phoneVerificationEnabled: z.boolean(),
});
export const updateCustomerSettingsSchema = customerSettingsSchema;
//# sourceMappingURL=contract.js.map